'use client'

import { useState, useMemo, useEffect } from 'react'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

interface DateEvent {
  id: string
  start?: number
  startDate?: string
  title?: string
  name?: string
  sold?: string
  availableQuantity?: number
  quantity?: number
}

interface DatePillStripProps {
  dates: DateEvent[]
  selectedDate: DateEvent | null
  onSelect: (date: DateEvent) => void
}

const DatePillStrip = ({ dates, selectedDate, onSelect }: DatePillStripProps) => {
  const sortedDates = useMemo(() => {
    if (!dates || dates.length === 0) return []
    return [...dates].sort((a, b) => (a.start || 0) - (b.start || 0))
  }, [dates])

  const dateMap = useMemo(() => {
    const map: Record<string, DateEvent> = {}
    for (const d of sortedDates) {
      if (!d.start) continue
      const dt = new Date(d.start * 1000)
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`
      map[key] = d
    }
    return map
  }, [sortedDates])

  const initialDate = useMemo(() => {
    if (selectedDate?.start) return new Date(selectedDate.start * 1000)
    if (sortedDates.length > 0 && sortedDates[0].start) return new Date(sortedDates[0].start * 1000)
    return new Date()
  }, [selectedDate, sortedDates])

  const [viewMonth, setViewMonth] = useState(initialDate.getMonth())
  const [viewYear, setViewYear] = useState(initialDate.getFullYear())
  const [animating, setAnimating] = useState(false)
  const [slideDirection, setSlideDirection] = useState('')

  // Sync calendar view when dates load asynchronously (e.g. first available date arrives)
  useEffect(() => {
    setViewMonth(initialDate.getMonth())
    setViewYear(initialDate.getFullYear())
  }, [initialDate])

  if (sortedDates.length === 0) return null

  const firstEventDate = sortedDates[0].start ? new Date(sortedDates[0].start * 1000) : new Date()
  const lastEventDate = sortedDates[sortedDates.length - 1].start ? new Date(sortedDates[sortedDates.length - 1].start * 1000) : new Date()
  const canGoBack = viewYear > firstEventDate.getFullYear() || (viewYear === firstEventDate.getFullYear() && viewMonth > firstEventDate.getMonth())
  const canGoForward = viewYear < lastEventDate.getFullYear() || (viewYear === lastEventDate.getFullYear() && viewMonth < lastEventDate.getMonth())

  const goToPrevMonth = () => {
    if (!canGoBack || animating) return
    setSlideDirection('slide-right')
    setAnimating(true)
    setTimeout(() => {
      if (viewMonth === 0) {
        setViewMonth(11)
        setViewYear(viewYear - 1)
      } else {
        setViewMonth(viewMonth - 1)
      }
      setSlideDirection('')
      setAnimating(false)
    }, 200)
  }

  const goToNextMonth = () => {
    if (!canGoForward || animating) return
    setSlideDirection('slide-left')
    setAnimating(true)
    setTimeout(() => {
      if (viewMonth === 11) {
        setViewMonth(0)
        setViewYear(viewYear + 1)
      } else {
        setViewMonth(viewMonth + 1)
      }
      setSlideDirection('')
      setAnimating(false)
    }, 200)
  }

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()

  const calendarCells: (null | { day: number; eventDate: DateEvent | null; isSoldOut: boolean })[] = []
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarCells.push(null)
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const key = `${viewYear}-${viewMonth}-${day}`
    const eventDate = dateMap[key] || null
    const isSoldOut = eventDate?.sold === '1'
    calendarCells.push({ day, eventDate, isSoldOut })
  }

  const selectedKey = selectedDate?.start
    ? (() => {
        const dt = new Date(selectedDate.start * 1000)
        return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`
      })()
    : null

  const availableThisMonth = calendarCells.filter(c => c && c.eventDate && !c.isSoldOut).length

  return (
    <div className="animate-slide-up-expand">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoBack}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-tertiary transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="text-base font-heading font-bold text-text-primary">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </div>
        <button
          onClick={goToNextMonth}
          disabled={!canGoForward}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-tertiary transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1.5">
        {DAY_LABELS.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-text-tertiary py-1.5">
            {d}
          </div>
        ))}
      </div>

      <div
        className={`grid grid-cols-7 gap-1 transition-[opacity,transform] duration-200 ${
          slideDirection === 'slide-left' ? 'opacity-0 -translate-x-2' :
          slideDirection === 'slide-right' ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'
        }`}
      >
        {calendarCells.map((cell, i) => {
          if (!cell) {
            return <div key={`empty-${i}`} className="aspect-square" />
          }

          const { day, eventDate, isSoldOut } = cell
          const cellKey = `${viewYear}-${viewMonth}-${day}`
          const isSelected = selectedKey === cellKey
          const isAvailable = !!eventDate && !isSoldOut

          return (
            <button
              key={cellKey}
              onClick={() => isAvailable && eventDate && onSelect(eventDate)}
              disabled={!isAvailable}
              className={`
                relative aspect-square rounded-xl flex items-center justify-center text-sm font-semibold transition-[background-color,border-color,color,transform,box-shadow] duration-200
                ${isSelected
                  ? 'bg-primary text-text-inverse font-bold shadow-md scale-105 border border-primary'
                  : isAvailable
                    ? 'text-text-primary border border-surface-border hover:border-primary hover:bg-primary/10 hover:scale-105 cursor-pointer'
                    : isSoldOut
                      ? 'text-text-tertiary/40 line-through cursor-not-allowed'
                      : 'text-text-tertiary/30 cursor-default'
                }
              `}
            >
              {day}
              {isAvailable && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary" />
              )}
            </button>
          )
        })}
      </div>

      {availableThisMonth > 0 ? (
        <div className="mt-3">
          <div className="text-xs text-text-tertiary text-center flex items-center justify-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
            {availableThisMonth} date{availableThisMonth !== 1 ? 's' : ''} available
          </div>
        </div>
      ) : (
        <div className="text-xs text-text-tertiary text-center mt-3">
          No dates available this month
        </div>
      )}
    </div>
  )
}

export default DatePillStrip
